import React from "react";
import { Grid, Typography, SxProps } from "@mui/material";
import Stack from "@mui/material/Stack";
import theme from "Theme/AppTheme";
import { getSpeakerColorByChannelOrPersona } from "Theme/utils";
import AcxChip from "../AcxChip";
import { SpeakerChannelMap } from "../AcxTranscription/Stores/SpeakerStore/SpeakerStore";
import {
    Blurb,
    ChatBlurb,
} from "../AcxTranscription/Components/BlurbBox/Blurb";
import { durationDisplay } from "components/SoundClipEditor/Controls/Player";
import { sxClsx } from "utils/styleClsx";
import { P2BlurbText } from "./P2BlurbText";
import moment from "moment";

interface BaseProps {
    blurb: Blurb | ChatBlurb;
    className?: string;
    sx?: SxProps;
    speakerChannelMap?: SpeakerChannelMap;
    variant?: "eddy" | "adverse";
    onClick?: (blurb: Blurb | ChatBlurb) => void;
    dataRange: { startWordIndex: number; endWordIndex: number };
}

export default function P2BlurbBox({
    blurb,
    className,
    variant,
    speakerChannelMap,
    sx,
    onClick,
    dataRange,
}: BaseProps) {
    const persona = (blurb as ChatBlurb).from ?? undefined;
    const channel = (blurb as Blurb).channel ?? undefined;
    const timestamp =
        (blurb as ChatBlurb).timestamp ??
        durationDisplay((blurb as Blurb).timeInterval[0]);

    const speaker = speakerChannelMap?.get(channel ?? -1);

    // Compute persona color inline
    const personaColor = !!persona
        ? getSpeakerColorByChannelOrPersona(persona)
        : getSpeakerColorByChannelOrPersona(channel);

    return (
        <Grid
            container
            className={className}
            sx={sxClsx(
                {
                    position: "relative",
                    padding: theme.spacing(2, 0),
                    borderRadius: 1,
                    paddingLeft: theme.spacing(1),
                    ...sx,
                },
                onClick && {
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                        cursor: "pointer",
                        backgroundColor: theme.palette.gray[50],
                    },
                },
            )}
            onClick={() => {
                onClick?.(blurb);
            }}
        >
            <Stack direction="row">
                <Stack
                    direction="column"
                    sx={{
                        width: "90px",
                        minWidth: "90px",
                        justifyContent: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: "bold",
                            color: personaColor,
                            paddingRight: "4px",
                        }}
                    >
                        {!!persona
                            ? persona
                            : speaker
                            ? speaker.name
                            : channel
                            ? `Speaker ${channel + 1}`
                            : "Speaker"}
                    </Typography>
                    <Typography sx={{ color: "gray" }}>
                        {persona
                            ? moment(timestamp).format("hh:mm")
                            : timestamp}
                    </Typography>
                </Stack>
                <Typography
                    sx={sxClsx(
                        {
                            paddingRight: "90px",
                            paddingLeft: theme.spacing(1),
                        },
                        variant === "eddy" && {
                            borderLeft: `2px solid ${theme.palette.highlight.eddy.main}`,
                        },
                        variant === "adverse" && {
                            borderLeft: `2px solid ${theme.palette.highlight.adverseEvent.main}`,
                        },
                    )}
                    component="div"
                >
                    <P2BlurbText blurb={blurb} dataRange={dataRange} />
                    <Grid sx={{ paddingTop: theme.spacing(1) }}>
                        {variant === "eddy" ? (
                            <AcxChip
                                color="eddy"
                                icon="eddy"
                                size="small"
                                label="Eddy Effect Signal"
                            />
                        ) : (
                            <AcxChip
                                color="adverse"
                                size="small"
                                label="Safety Event"
                            />
                        )}
                    </Grid>
                </Typography>
            </Stack>
        </Grid>
    );
}
